import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import { useMutation } from "react-query";
import PageHeader from "Components/Pages/Header";
import Breadcrumb from "Components/Shared/Breadcrumb";
import { Box } from "styles/globalComponents";
import { Button } from "Components/Button";
import { Send } from "lucide-react";
import ApiChat, { ApiChatFeedback } from "Services/ApiChat";
import * as S from "./styles";

interface Message {
  id: string;
  text: string;
  sender: "user" | "bot";
  timestamp: Date;
  apiMessageId?: string;
  feedbackRequired?: boolean;
  feedbackStatus?: "pending" | "sent";
  feedbackValue?: "bom" | "ruim";
  feedbackReason?: string;
  showReasonInput?: boolean;
  feedbackError?: string;
  intent?: string;
  precisaHandoff?: boolean;
  notaJuiz?: number | null;
  justificativaJuiz?: string | null;
}

interface RequestLikeError {
  isAxiosError?: boolean;
  response?: unknown;
}

export const Chat = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "initial-bot",
      text: "Olá! Como posso te ajudar hoje?",
      sender: "bot",
      timestamp: new Date(),
    },
  ]);

  const [sendingFeedbackId, setSendingFeedbackId] = useState<string | null>(
    null
  );

  const [inputValue, setInputValue] = useState("");

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const userData = useSelector(
    (state: {
      User?: { userData?: { id?: string | number; name?: string } };
    }) => state.User?.userData
  );

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // const hasPendingFeedback = useMemo(
  //   () =>
  //     messages.some(
  //       (message) =>
  //         message.sender === "bot" &&
  //         message.feedbackRequired &&
  //         message.feedbackStatus === "pending"
  //     ),
  //   [messages]
  // );

  const { mutateAsync: sendChatMessage, isLoading: isSendingMessage } =
    useMutation((question: string) =>
      ApiChat.post("/chat", {
        pergunta: question,
      })
    );

  const { mutateAsync: sendFeedback } = useMutation(
    ({
      id,
      feedback,
      question,
      answer,
      comment,
      user_id,
      user_name,
      date_time,
    }: {
      id: string;
      feedback: "bom" | "ruim";
      question: string;
      answer: string;
      comment: string;
      user_id?: string | number;
      user_name?: string;
      date_time: string;
    }) =>
      ApiChatFeedback.post("/feedbacks", {
        id,
        feedback,
        question,
        answer,
        comment,
        user_id,
        user_name,
        date_time,
      })
  );

  const updateMessage = useCallback(
    (messageId: string, updater: (message: Message) => Message) => {
      setMessages((prevMessages) =>
        prevMessages.map((message) =>
          message.id === messageId ? updater(message) : message
        )
      );
    },
    []
  );

  const isRequestWithoutResponse = useCallback((error: unknown) => {
    if (!error || typeof error !== "object") {
      return false;
    }

    const requestError = error as RequestLikeError;

    return Boolean(requestError.isAxiosError) && !requestError.response;
  }, []);

  const handleSendMessage = useCallback(async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isSendingMessage) {
      return;
    }

    try {
      // 1. Criar node da mensagem do usuário
      const userMessage: Message = {
        id: `user-${Date.now()}-${Math.random()}`,
        text: trimmedInput,
        sender: "user",
        timestamp: new Date(),
      };

      setMessages((prevMessages) => [...prevMessages, userMessage]);

      setInputValue("");

      const response = await sendChatMessage(trimmedInput);

      const botMessage: Message = {
        id: `bot-${Date.now()}-${Math.random()}`,
        text:
          response.data?.answer ||
          response.data?.resposta ||
          response.data?.message ||
          response.data?.response ||
          response.data?.text ||
          "Desculpe, não consegui processar sua mensagem.",
        sender: "bot",
        timestamp: new Date(),
        apiMessageId: response.data?.id,
        feedbackRequired: Boolean(response.data?.id),
        feedbackStatus: response.data?.id ? "pending" : undefined,
        intent: response.data?.intent || undefined,
        precisaHandoff: Boolean(response.data?.precisa_handoff),
        notaJuiz: response.data?.nota_juiz ?? null,
        justificativaJuiz: response.data?.justificativa_juiz ?? null,
      };

      setMessages((prevMessages) => [...prevMessages, botMessage]);
    } catch (error: unknown) {
      const errorMessage: Message = {
        id: `bot-error-${Date.now()}-${Math.random()}`,
        text: "Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente.",
        sender: "bot",
        timestamp: new Date(),
      };

      setMessages((prevMessages) => [...prevMessages, errorMessage]);
    }
  }, [inputValue, isSendingMessage, sendChatMessage]);

  const submitFeedback = useCallback(
    async (messageId: string, feedbackValue: "bom" | "ruim", reason = "") => {
      const targetMessageIndex = messages.findIndex(
        (message) => message.id === messageId
      );
      const targetMessage =
        targetMessageIndex >= 0 ? messages[targetMessageIndex] : undefined;
      if (!targetMessage || !targetMessage.apiMessageId) {
        return;
      }

      const previousUserMessage =
        targetMessageIndex > 0
          ? [...messages]
              .slice(0, targetMessageIndex)
              .reverse()
              .find((message) => message.sender === "user")
          : undefined;

      if (feedbackValue === "ruim" && !reason.trim()) {
        updateMessage(messageId, (message) => ({
          ...message,
          feedbackError: "Informe o motivo para avaliação negativa.",
          showReasonInput: true,
        }));
        return;
      }

      try {
        setSendingFeedbackId(messageId);
        updateMessage(messageId, (message) => ({
          ...message,
          feedbackError: "",
        }));

        const feedbackResponse = await sendFeedback({
          id: targetMessage.apiMessageId,
          feedback: feedbackValue,
          question: previousUserMessage?.text || "",
          answer: targetMessage.text,
          comment: reason.trim(),
          user_id: userData?.id,
          user_name: userData?.name,
          date_time: new Date().toISOString(),
        });

        if (!feedbackResponse?.data?.data?.ok) {
          throw new Error("Feedback not acknowledged by API");
        }

        updateMessage(messageId, (message) => ({
          ...message,
          feedbackStatus: "sent",
          feedbackValue,
          feedbackReason: reason.trim(),
          showReasonInput: false,
          feedbackError: "",
        }));
      } catch (error: unknown) {
        const feedbackErrorMessage = isRequestWithoutResponse(error)
          ? "O feedback pode ter sido enviado, mas a resposta foi bloqueada por CORS na API de feedback."
          : "Não foi possível salvar o feedback. Tente novamente.";

        updateMessage(messageId, (message) => ({
          ...message,
          feedbackError: feedbackErrorMessage,
        }));
      } finally {
        setSendingFeedbackId(null);
      }
    },
    [isRequestWithoutResponse, messages, updateMessage, sendFeedback, userData]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !e.shiftKey && !isSendingMessage) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage, isSendingMessage]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const isButtonDisabled = useMemo(
    () => isSendingMessage || !inputValue.trim(),
    [isSendingMessage, inputValue]
  );

  return (
    <S.PageContainer className="entry-app-content">
      <div className="content-holder">
        <div className="wrapper-header-content">
          <PageHeader title="Labi IA">
            <Breadcrumb list={[]} />
          </PageHeader>
        </div>

        <S.PageContent>
          <div className="chat-container scroll-purple">
            <div className="chat-messages">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={
                    message.sender === "user" ? "chat-user" : "chat-bot"
                  }
                >
                  <div className="message-wrapper">
                    <div className="message">{message.text}</div>

                    {message.sender === "bot" &&
                      message.feedbackRequired &&
                      message.feedbackStatus === "pending" && (
                        <div className="feedback-area">
                          {/* <div className="feedback-actions">
                            <button
                              type="button"
                              className={`feedback-btn ${
                                message.feedbackValue === "bom"
                                  ? "active-good"
                                  : ""
                              }`}
                              onClick={() => submitFeedback(message.id, "bom")}
                              disabled={sendingFeedbackId === message.id}
                              aria-label="Avaliar resposta como boa"
                            >
                              <ThumbsUp size={16} />
                            </button>

                            <button
                              type="button"
                              className={`feedback-btn ${
                                message.feedbackValue === "ruim"
                                  ? "active-bad"
                                  : ""
                              }`}
                              onClick={() =>
                                updateMessage(message.id, (target) => ({
                                  ...target,
                                  showReasonInput: true,
                                  feedbackError: "",
                                }))
                              }
                              disabled={sendingFeedbackId === message.id}
                              aria-label="Avaliar resposta como ruim"
                            >
                              <ThumbsDown size={16} />
                            </button>
                          </div> */}

                          {/* {message.showReasonInput && (
                            <div className="feedback-reason">
                              <textarea
                                placeholder="Por que a resposta foi ruim?"
                                value={message.feedbackReason || ""}
                                onChange={(event) => {
                                  const { value } = event.target;
                                  updateMessage(message.id, (target) => ({
                                    ...target,
                                    feedbackReason: value,
                                    feedbackError: "",
                                  }));
                                }}
                                maxLength={500}
                              />
                              <div className="feedback-reason-actions">
                                <button
                                  type="button"
                                  className="save"
                                  onClick={() =>
                                    submitFeedback(
                                      message.id,
                                      "ruim",
                                      message.feedbackReason || ""
                                    )
                                  }
                                  disabled={sendingFeedbackId === message.id}
                                >
                                  Enviar avaliação
                                </button>
                                <button
                                  type="button"
                                  className="cancel"
                                  onClick={() =>
                                    updateMessage(message.id, (target) => ({
                                      ...target,
                                      showReasonInput: false,
                                      feedbackReason: "",
                                      feedbackError: "",
                                    }))
                                  }
                                  disabled={sendingFeedbackId === message.id}
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          )} */}

                          {/* {message.feedbackError && (
                            <p className="feedback-error">
                              {message.feedbackError}
                            </p>
                          )} */}
                        </div>
                      )}

                    {message.sender === "bot" &&
                      message.feedbackStatus === "sent" && (
                        <div className="feedback-status">
                          {message.feedbackValue === "bom"
                            ? "Avaliado como boa resposta"
                            : "Avaliado como resposta ruim"}
                        </div>
                      )}
                  </div>
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>

            {isSendingMessage && (
              <S.Loading>
                <div className="typing-indicator">
                  <div className="typing-dots">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
                Digitando...
              </S.Loading>
            )}
          </div>

          <Box className="ask-box" display="flex">
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Digite aqui sua mensagem..."
              disabled={isSendingMessage}
              maxLength={1000}
              aria-label="Campo de mensagem do chat"
            />

            <Button onClick={handleSendMessage} disabled={isButtonDisabled}>
              <Send size={20} />
            </Button>
          </Box>

          {/* {hasPendingFeedback && (
            <S.FeedbackBlockMessage>
              Avalie a ultima resposta (positiva ou negativa) para liberar o
              envio da proxima mensagem.
            </S.FeedbackBlockMessage>
          )} */}
        </S.PageContent>
      </div>
    </S.PageContainer>
  );
};
